/* ============================================================
   ARTISAN DELI MARKET — when a hamper can actually land.

   ONE implementation, both paths. The composed path (/create/) and the
   ready-made path (/basket/) each had their own copy of this, and they
   immediately drifted: the Composer was taught about bank holidays and the
   basket wasn't, so the same shop would refuse Christmas Day on a hamper you
   built and sell it on a hamper you picked. Same failure as the header that
   used to be pasted into every page — so it lives here now, like the chrome.

   What is actually true, per _FACTS.md (verified against the shop's published
   shipping policy and its Shopify rate names, 2026-07-27):

     · The shop sells a named weekday, or a Saturday. There is NO Sunday
       service of any kind.
     · The van leaves Monday–Friday, at one. Miss it and the next one is real.
     · Named Day is an aim, not a guarantee — never write "guaranteed" against
       a date.

   Delivery days are Tuesday to Saturday: no Sunday, no Monday. That is now
   SOURCED, not inferred — Verity, call 2026-09-29 (recall #3867 @29:19): "you
   can't select Sundays or Mondays as your delivery day" (_FACTS.md, Delivery).
   Monday used to fall out of the grid only because its eve is a Sunday and no
   van runs then; it is excluded BY NAME below as well, so a future change to
   the dispatch rule (a Sunday van, a moved cut-off) cannot quietly reopen it.
   Pages may say "Tuesday to Saturday"; they may not say why.

   Christmas Eve is never a delivery day — Verity, 27 Oct 2026: "remove 24th
   december as an option on the delivery calender". It is closed for delivery
   only: a van may still leave that day, and it is not called a bank holiday.
   The rest of the shop's Christmas trading break is NOT in here. Nobody has
   told us those dates and we will not invent them.
   ============================================================ */
(function (root) {
  'use strict';

  function dOnly(d) { return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }
  // Dispatch happens in Sussex. A customer ordering from abroad must see the
  // same cutoff and available dates as a customer in the UK.
  function nowUK() {
    var parts = new Intl.DateTimeFormat('en-GB', {timeZone:'Europe/London',year:'numeric',month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit',second:'2-digit',hourCycle:'h23'}).formatToParts(new Date());
    var p={}; parts.forEach(function(x){p[x.type]=x.value;});
    return new Date(+p.year,+p.month-1,+p.day,+p.hour,+p.minute,+p.second);
  }
  function iso(d) {
    return d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2);
  }

  /* Easter is computed, not typed — a hard-coded list rots after one year.
     (Anonymous Gregorian algorithm.) */
  function easterSunday(y) {
    var a = y % 19, b = Math.floor(y / 100), c = y % 100,
        d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25),
        g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30,
        i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7,
        m = Math.floor((a + 11 * h + 22 * l) / 451);
    return new Date(y, Math.floor((h + l - 7 * m + 114) / 31) - 1, ((h + l - 7 * m + 114) % 31) + 1);
  }

  var holCache = {};
  function holidays(y) {
    if (holCache[y]) return holCache[y];
    var set = {};
    function add(d, name) { if (!set[iso(d)]) set[iso(d)] = name; }
    /* a public holiday landing ON A WEEKEND moves to the next free weekday —
       and only then: a Thursday Christmas takes no extra day off with it */
    function subst(d, name) {
      if (d.getDay() !== 0 && d.getDay() !== 6) return;
      var s = new Date(d.getTime());
      while (s.getDay() === 0 || s.getDay() === 6 || set[iso(s)]) s.setDate(s.getDate() + 1);
      add(s, name + ' (the day off for it)');
    }
    var nyd = new Date(y, 0, 1); add(nyd, 'New Year’s Day'); subst(nyd, 'New Year’s Day');
    var es = easterSunday(y);
    add(new Date(y, es.getMonth(), es.getDate() - 2), 'Good Friday');
    add(new Date(y, es.getMonth(), es.getDate() + 1), 'Easter Monday');
    var em = new Date(y, 4, 1); while (em.getDay() !== 1) em.setDate(em.getDate() + 1);
    add(em, 'the early May bank holiday');
    var lm = new Date(y, 5, 0); while (lm.getDay() !== 1) lm.setDate(lm.getDate() - 1);
    add(lm, 'the spring bank holiday');
    var la = new Date(y, 8, 0); while (la.getDay() !== 1) la.setDate(la.getDate() - 1);
    add(la, 'the August bank holiday');
    var xd = new Date(y, 11, 25), bd = new Date(y, 11, 26);
    add(xd, 'Christmas Day'); add(bd, 'Boxing Day');
    subst(xd, 'Christmas Day'); subst(bd, 'Boxing Day');
    holCache[y] = set; return set;
  }

  function holidayName(d) { return holidays(d.getFullYear())[iso(d)] || ''; }
  function isHoliday(d) { return !!holidayName(d); }

  /* the van leaves once a weekday, at one — miss it and the next is real */
  function nextDispatch() {
    var now = nowUK(), d = dOnly(now), wd = d.getDay();
    if (wd === 0 || wd === 6 || isHoliday(d) || now.getHours() >= 13) {
      do { d.setDate(d.getDate() + 1); } while (d.getDay() === 0 || d.getDay() === 6 || isHoliday(d));
    }
    return d;
  }

  /* A day can carry a hamper iff it is Tuesday to Saturday, it isn't a bank
     holiday or Christmas Eve, and a van could actually have left the day before. */
  function daySelectable(d) {
    if (d.getDay() === 0) return false;                        /* no Sunday service */
    if (d.getDay() === 1) return false;                        /* no Monday delivery — SOURCED, Verity @29:19 */
    if (isHoliday(d)) return false;
    if (d.getMonth() === 11 && d.getDate() === 24) return false;  /* no Christmas Eve delivery — Verity, 27 Oct 2026 */
    var eve = new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1);
    var ed = eve.getDay();
    if (ed === 0 || ed === 6 || isHoliday(eve)) return false;   /* no van that day */
    return eve.getTime() >= nextDispatch().getTime();
  }

  function earliestDay() {
    var d = dOnly(nowUK());
    for (var i = 0; i < 30; i++) {
      d.setDate(d.getDate() + 1);
      if (daySelectable(d)) return new Date(d.getTime());
    }
    return d;
  }

  /* The month a calendar opens on: the one holding the first day a hamper can
     land. Opening on the current month stranded every customer at a month's
     end — 29 September 2026 showed September with no selectable day at all,
     "To the basket" quietly refused, and it read as a broken site. */
  function firstOpenMonth() {
    var e = earliestDay();
    return new Date(e.getFullYear(), e.getMonth(), 1);
  }

  /* How long until the soonest day can next move: one o'clock on a day a van
     leaves (the van goes), otherwise midnight (a new day). A page left open
     across either kept offering a day whose van had gone, and kept saying
     "order by 1pm today" at ten past (review item 23). A page redraws itself
     then, instead of ticking a countdown — the design system bans those. The
     second of slack means the redraw lands after the change, never before;
     one landing early (a clock change) finds nothing moved and asks again. */
  function msToChange() {
    var now = nowUK(), d = dOnly(now), wd = d.getDay();
    var at = (wd !== 0 && wd !== 6 && !isHoliday(d) && now.getHours() < 13)
      ? new Date(d.getFullYear(), d.getMonth(), d.getDate(), 13)
      : new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
    return Math.max(1000, at.getTime() - now.getTime() + 1000);
  }

  /* £5.95 the days around it, £7.95 the named weekday, £12.95 the Saturday */
  function svcPrice(iso8601, svc) {
    if (!iso8601) return 0;
    var p = String(iso8601).split('-');
    if (new Date(+p[0], +p[1] - 1, +p[2]).getDay() === 6) return 12.95;   /* a Saturday is only ever the Saturday courier */
    return svc === 'std' ? 5.95 : 7.95;
  }

  root.ADMDelivery = {
    dOnly: dOnly, iso: iso, nowUK: nowUK,
    holidayName: holidayName, isHoliday: isHoliday,
    nextDispatch: nextDispatch, daySelectable: daySelectable,
    earliestDay: earliestDay, firstOpenMonth: firstOpenMonth, svcPrice: svcPrice,
    msToChange: msToChange
  };
})(window);
